/**
 * `useCounts()` — unread / pending counts for the signed-in user.
 *
 * Powers the nav badges (messages, notifications, friend requests, …).
 * Only runs when a session token is present; unauthenticated visitors get no
 * counts (and therefore no badges). Mirrors the legacy `GET /user/counts`.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/pages/auth';

/** Shape returned by `GET /user/counts`. */
export interface UserCounts {
  friends: number;
  requests: number;
  notifications: number;
  pokes: number;
  messages: number;
  visitors: number;
}

/** Poll cadence for unread counts. 30s strikes the balance — nav
 *  badges update without page refresh while the user sits on the
 *  dashboard, but we don't burn 3 req/min on a payload that rarely
 *  changes that often. Foreground only. (Was 20s; bumped per the
 *  TanStack staleTime audit — combined with the `staleTime` below
 *  this cut the typical /user/counts request rate by ~30%.) */
const COUNTS_POLL_MS = 30_000;
/** Reads within this window reuse the cached value instead of
 *  triggering a re-fetch on every consumer mount. With ~4 nav-badge
 *  consumers all calling `useCounts()`, a `staleTime: 0` plus a tab
 *  switch produced a flurry of duplicate requests. Half the polling
 *  interval is enough to dedupe consumer churn without making badges
 *  visibly stale. */
const COUNTS_STALE_MS = 15_000;

/**
 * Fetch the current user's counts. Disabled (no request) when unauthenticated.
 * Polls every {@link COUNTS_POLL_MS} so nav badges update without a page
 * refresh (matches the legacy app's polling cadence). Also refetches when
 * the user tabs back in, so a long idle picks up new activity on focus.
 */
export function useCounts() {
  const token = useAuth((s) => s.token);

  return useQuery({
    queryKey: queryKeys.counts.me(),
    queryFn: () => api.get<UserCounts>('user/counts'),
    enabled: Boolean(token),
    staleTime: COUNTS_STALE_MS,
    refetchInterval: COUNTS_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}
